// Calendario del kine. Showcase (02/10/2026): no existe modelo de turnos
// en la DB, así que los turnos se SIMULAN de forma determinística a partir
// de los pacientes reales del kine (mismo paciente + misma fecha → mismo
// turno en cada render/recarga). Si el proyecto se reactiva, esto se
// reemplaza por una tabla de turnos real.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronLeft, ChevronRight, Video, MapPin } from 'lucide-react';
import { Avatar } from '../components/Avatar';
import { KineHeader } from '../components/kine/KineHeader';
import { KineSidebar } from '../components/kine/KineSidebar';
import { useKineDashboard } from '@/lib/supabase/queries/useKineDashboard';

interface Turno {
  pacienteId: string;
  paciente: string;
  hora: string;
  tipo: 'Sesión presencial' | 'Seguimiento virtual' | 'Evaluación';
  duracionMin: number;
  colorIndex: number;
}

const DIAS_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const HORAS = ['08:30', '09:30', '10:30', '11:30', '14:00', '15:00', '16:30', '17:30', '18:30'];
const TIPOS: Turno['tipo'][] = ['Sesión presencial', 'Sesión presencial', 'Seguimiento virtual', 'Evaluación'];

/** Hash entero estable de un string (djb2). */
function hash(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Turnos de un día: cada paciente tiene 2 días fijos por semana (según su
 *  id). Domingos sin turnos, sábados solo a la mañana. */
function turnosDelDia(
  fecha: Date,
  pacientes: Array<{ id: string; nombre: string; apellido: string }>,
): Turno[] {
  const dow = fecha.getDay(); // 0 = domingo
  if (dow === 0) return [];
  const turnos: Turno[] = [];
  pacientes.forEach((p, idx) => {
    const h = hash(p.id);
    const diaA = (h % 6) + 1; // 1..6 (lun..sáb)
    const diaB = ((h >> 3) % 5) + 1; // 1..5 (lun..vie)
    if (dow !== diaA && dow !== diaB) return;
    const hDia = hash(p.id + dateKey(fecha));
    const horas = dow === 6 ? HORAS.slice(0, 3) : HORAS;
    const tipo = TIPOS[hDia % TIPOS.length];
    turnos.push({
      pacienteId: p.id,
      paciente: `${p.nombre} ${p.apellido}`,
      hora: horas[(h + dow) % horas.length],
      tipo,
      duracionMin: tipo === 'Evaluación' ? 60 : tipo === 'Seguimiento virtual' ? 30 : 45,
      colorIndex: idx,
    });
  });
  return turnos.sort((a, b) => a.hora.localeCompare(b.hora));
}

export function CalendarioKine() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pacientes, metricas } = useKineDashboard();
  const hoy = new Date();
  const [mes, setMes] = useState(() => new Date(hoy.getFullYear(), hoy.getMonth(), 1));
  const [seleccionado, setSeleccionado] = useState(hoy);

  const activos = useMemo(
    () => (pacientes ?? []).filter((p) => p.invitationStatus === 'active'),
    [pacientes],
  );

  // Celdas del mes: offset para arrancar en lunes + días del mes.
  const offset = (mes.getDay() + 6) % 7;
  const diasEnMes = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate();
  const celdas: Array<Date | null> = [
    ...Array.from({ length: offset }, () => null),
    ...Array.from(
      { length: diasEnMes },
      (_, i) => new Date(mes.getFullYear(), mes.getMonth(), i + 1),
    ),
  ];

  const turnosSeleccionado = turnosDelDia(seleccionado, activos);
  const esHoy = (d: Date) => dateKey(d) === dateKey(hoy);
  const tituloMes = mes.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  const tituloDia = seleccionado.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const cambiarMes = (delta: number) =>
    setMes(new Date(mes.getFullYear(), mes.getMonth() + delta, 1));

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={metricas?.kineNombre || '—'}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="calendario"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-4 md:p-8 min-w-0">
          <div className="max-w-[1200px]">
            <h1 className="text-3xl mb-6">Calendario</h1>

            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
              <div className="bg-card rounded-2xl p-4 md:p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold capitalize">{tituloMes}</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => cambiarMes(-1)}
                      aria-label="Mes anterior"
                      className="p-2 hover:bg-muted rounded-lg transition-all duration-200 ease-out"
                    >
                      <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => cambiarMes(1)}
                      aria-label="Mes siguiente"
                      className="p-2 hover:bg-muted rounded-lg transition-all duration-200 ease-out"
                    >
                      <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
                  {DIAS_SEMANA.map((day) => (
                    <div key={day} className="text-center text-xs uppercase tracking-[0.08em] text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1 md:gap-2">
                  {celdas.map((d, i) => {
                    if (!d) return <div key={`v${i}`} />;
                    const cantidad = turnosDelDia(d, activos).length;
                    const selected = dateKey(d) === dateKey(seleccionado);
                    return (
                      <button
                        key={dateKey(d)}
                        onClick={() => setSeleccionado(d)}
                        className={`aspect-square flex flex-col items-center justify-center gap-1 rounded-lg text-sm transition-all duration-200 ease-out ${
                          selected
                            ? 'bg-primary text-primary-foreground font-semibold'
                            : esHoy(d)
                              ? 'ring-2 ring-primary/40 font-semibold'
                              : 'hover:bg-muted'
                        }`}
                      >
                        {d.getDate()}
                        {cantidad > 0 && (
                          <span className="flex gap-0.5">
                            {Array.from({ length: Math.min(cantidad, 3) }, (_, k) => (
                              <span
                                key={k}
                                className={`h-1 w-1 rounded-full ${selected ? 'bg-primary-foreground' : 'bg-primary/60'}`}
                              />
                            ))}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-card rounded-2xl p-4 md:p-6 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]">
                <h2 className="text-lg font-semibold mb-1 capitalize">
                  {esHoy(seleccionado) ? `Hoy · ${tituloDia}` : tituloDia}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  {turnosSeleccionado.length === 0
                    ? 'Sin turnos'
                    : `${turnosSeleccionado.length} ${turnosSeleccionado.length === 1 ? 'turno' : 'turnos'}`}
                </p>
                <div className="space-y-3">
                  {turnosSeleccionado.map((t) => (
                    <button
                      key={t.pacienteId}
                      onClick={() => navigate(`/paciente/${t.pacienteId}`)}
                      className="w-full text-left p-4 bg-muted/60 rounded-xl hover:bg-muted transition-all duration-200 ease-out flex items-center gap-3"
                    >
                      <Avatar name={t.paciente} colorIndex={t.colorIndex} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="font-medium truncate">{t.paciente}</p>
                          <span className="text-xs font-semibold tabular-nums text-primary shrink-0">
                            {t.hora}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                          {t.tipo === 'Seguimiento virtual' ? (
                            <Video className="w-3.5 h-3.5" strokeWidth={1.5} />
                          ) : (
                            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                          )}
                          {t.tipo} · {t.duracionMin} min
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
